
import { PropType, computed, defineComponent, onMounted, ref } from 'vue';

interface WindowPosition {
  x: number;
  y: number;
}

interface WindowSize {
  height: number;
  width: number;
}

export default defineComponent({
  name: 'FloatingWindow',
  props: {
    resizable: {
      type: Boolean,
      default: true,
    },
    position: {
      type: Object as PropType<WindowPosition>,
      default: () => ({ x: 0, y: 0 }),
    },
    size: {
      type: Object as PropType<WindowSize>,
      default: () => ({ height: 1024, width: 768 }),
    },
    title: {
      type: String,
    },
  },

  setup(props, { emit }) {
    const innerPosition = ref(props.position);
    const innerSize = ref(props.size);

    const fullscreenMode = ref(false);

    const computedContainerStyle = computed(() => {
      if (fullscreenMode.value) {
        return {
          top: '0px',
          left: '0px',
          height: '100dvh',
          width: '100dvw',
        };
      }
      return {
        top: `${innerPosition.value.y}px`,
        left: `${innerPosition.value.x}px`,
        height: `${innerSize.value.height}px`,
        width: `${innerSize.value.width}px`,
      };
    });

    const computedBorderStyle = computed(() => {
      if (fullscreenMode.value) {
        return {
          height: '100dvh',
          width: '100dvw',
        };
      }
      return {
        height: `${innerSize.value.height - 2}px`,
        width: `${innerSize.value.width - 2}px`,
      };
    });

    const dragging = ref(false);
    const resizing = ref(false);

    const startDraggingCursorPosition = ref({ x: 0, y: 0 });
    const startDraggingWindowPosition = ref({ x: 0, y: 0 });
    const startResizingCursorPosition = ref({ x: 0, y: 0 });
    const startResizingWindowSize = ref({ height: 0, width: 0 });

    function startDragging(event: MouseEvent | TouchEvent) {
      const point = event instanceof MouseEvent ? event : event.touches[0];
      dragging.value = true;
      startDraggingCursorPosition.value.x = point.clientX;
      startDraggingCursorPosition.value.y = point.clientY;
      startDraggingWindowPosition.value.x = innerPosition.value.x;
      startDraggingWindowPosition.value.y = innerPosition.value.y;
      console.debug('Start dragging at position', event, startDraggingCursorPosition.value);
      document.addEventListener('mousemove', setNewWindowPosition);
      document.addEventListener('mouseup', stopDragging);
      document.addEventListener('touchstop', stopDragging);
      document.addEventListener('touchmove', setNewWindowPosition);
    }

    function setNewWindowPosition(event: MouseEvent | TouchEvent) {
      const point = event instanceof MouseEvent ? event : event.touches[0];
      const startCursorX = startDraggingCursorPosition.value.x;
      const startCursorY = startDraggingCursorPosition.value.y;
      const currentCursorX = point.clientX;
      const currentCursorY = point.clientY;
      const diffX = currentCursorX - startCursorX;
      const diffY = currentCursorY - startCursorY;
      // console.debug('Moving :', currentCursorX, currentCursorY);
      const newX: number = startDraggingWindowPosition.value.x + diffX;
      const newY = startDraggingWindowPosition.value.y + diffY;

      innerPosition.value.x = Math.max(0, newX);
      innerPosition.value.y = Math.max(0, newY);
    }

    function stopDragging(event: MouseEvent) {
      // console.debug('Stop dragging at position', startDraggingCursorPosition.value);
      dragging.value = false;
      startDraggingCursorPosition.value.x = 0;
      startDraggingCursorPosition.value.y = 0;
      startDraggingWindowPosition.value.x = 0;
      startDraggingWindowPosition.value.y = 0;
      document.removeEventListener('mouseup', stopDragging);
      document.removeEventListener('mousemove', setNewWindowPosition);
      document.removeEventListener('touchstop', stopDragging);
      document.removeEventListener('touchmove', setNewWindowPosition);
    }

    function startResizing(event: MouseEvent | TouchEvent) {
      const point = event instanceof MouseEvent ? event : event.touches[0];
      dragging.value = true;
      startResizingCursorPosition.value.x = point.clientX;
      startResizingCursorPosition.value.y = point.clientY;
      startResizingWindowSize.value.width = innerSize.value.width;
      startResizingWindowSize.value.height = innerSize.value.height;
      console.debug('Start resizing at position', startDraggingCursorPosition.value);
      document.addEventListener('mouseup', stopResizing);
      document.addEventListener('mousemove', setNewWindowSize);
      document.addEventListener('touchstop', stopResizing);
      document.addEventListener('touchmove', setNewWindowSize);
    }

    function setNewWindowSize(event: MouseEvent | TouchEvent) {
      const point = event instanceof MouseEvent ? event : event.touches[0];
      const startCursorX = startResizingCursorPosition.value.x;
      const startCursorY = startResizingCursorPosition.value.y;
      const currentCursorX = point.clientX;
      const currentCursorY = point.clientY;
      const diffX = currentCursorX - startCursorX;
      const diffY = currentCursorY - startCursorY;

      const newWidth = startResizingWindowSize.value.width + diffX;
      const newHeight = startResizingWindowSize.value.height + diffY;
      // Защита от того что бы свернули его в точку
      innerSize.value.width = Math.max(100, newWidth);
      innerSize.value.height = Math.max(100, newHeight);
    }

    function stopResizing(event: MouseEvent) {
      dragging.value = false;
      startResizingCursorPosition.value.x = 0;
      startResizingCursorPosition.value.y = 0;
      startResizingWindowSize.value.width = 0;
      startResizingWindowSize.value.height = 0;
      document.removeEventListener('mouseup', stopResizing);
      document.removeEventListener('mousemove', setNewWindowSize);
      document.removeEventListener('touchstop', stopResizing);
      document.removeEventListener('touchmove', setNewWindowSize);
    }

    onMounted(() => {
      const height = document.documentElement.clientHeight;
      const width = document.documentElement.clientWidth;

      innerSize.value.height = Math.min(innerSize.value.height, height) - 100;
      innerSize.value.width = Math.min(innerSize.value.width, width) - 100;

      innerPosition.value.x = Math.max(0, (width - innerSize.value.width) / 2);
      innerPosition.value.y = Math.max(0, (height - innerSize.value.height) / 2);
    });

    const fullscreenProcessing = ref(false);

    function fullscreenOn() {
      fullscreenProcessing.value = true;
      fullscreenMode.value = true;
    }
    function fullscreenOff() {
      fullscreenMode.value = false;
      setTimeout(() => {
        fullscreenProcessing.value = false;
      }, 200);
    }

    return {
      computedContainerStyle,
      dragging,
      resizing,
      startDragging,
      startResizing,
      computedBorderStyle,
      fullscreenMode,
      fullscreenProcessing,
      fullscreenOn,
      fullscreenOff,
    };
  },
});
